import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Avatar from './Avatar';
import './Profile.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [credentialsForm, setCredentialsForm] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { user, createCredentials } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setCredentialsForm({
      ...credentialsForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateCredentials = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    if (credentialsForm.password !== credentialsForm.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (credentialsForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    const result = await createCredentials(credentialsForm.username, credentialsForm.password);

    if (result.success) {
      setMessage('Username and password created successfully!');
      setCredentialsForm({ username: '', password: '', confirmPassword: '' });
    } else {
      setError(result.message);
    }

    setIsLoading(false);
  };

  const handleRefreshProfilePicture = async () => {
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh-profile-picture`);
      setMessage('Profile picture refreshed successfully!');
    } catch (error) {
      console.error('Refresh profile picture error:', error);
      setError(error.response?.data?.message || 'Failed to refresh profile picture');
    }

    setIsLoading(false);
  };

  return (
    <div className="profile-container">
      <header className="profile-header">
        <button 
          className="back-btn"
          onClick={() => navigate('/chat')}
        >
          ← Back to Chat
        </button>
        <h1>Profile Settings</h1>
      </header>

      <div className="profile-content">
        <div className="profile-tabs">
          <button 
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button 
            className={`tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
          <button 
            className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            Preferences
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'profile' && (
            <div className="profile-tab">
              <div className="profile-info">
                <div className="avatar-section">
                  <Avatar
                    user={user}
                    alt="Profile"
                    size={80}
                    className="profile-avatar"
                  />
                  {user?.googleId && (
                    <button 
                      className="refresh-avatar-btn"
                      onClick={handleRefreshProfilePicture}
                      disabled={isLoading}
                      title="Refresh profile picture from Google"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 4v6h-6"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      {isLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                  )}
                </div>
                <div className="info-section">
                  <div className="info-item">
                    <label>Display Name</label>
                    <div className="info-value">{user?.displayName}</div>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <div className="info-value">{user?.email}</div>
                  </div>
                  <div className="info-item">
                    <label>Username</label>
                    <div className="info-value">
                      {user?.username || 'Not set'}
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Role</label>
                    <div className="info-value role-badge">
                      {user?.role}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="security-tab">
              <h3>Create Username & Password</h3>
              <p className="section-description">
                Create a username and password to login without Google Sign-In.
                {user?.username && ' You already have credentials set up.'}
              </p>

              {message && (
                <div className="success-message">
                  {message}
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleCreateCredentials} className="credentials-form">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={credentialsForm.username}
                    onChange={handleInputChange}
                    placeholder={user?.username || 'Enter username'}
                    required
                    disabled={isLoading}
                    minLength={3}
                    maxLength={20}
                  />
                  <small>3-20 characters, must be unique</small>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={credentialsForm.password}
                    onChange={handleInputChange}
                    placeholder="Enter password"
                    required
                    disabled={isLoading}
                    minLength={6}
                  />
                  <small>At least 6 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={credentialsForm.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm password"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Credentials'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="preferences-tab">
              <h3>Chat Preferences</h3>
              <div className="preference-item">
                <label>
                  <input type="checkbox" defaultChecked={user?.preferences?.notifications} />
                  Enable Notifications
                </label>
              </div>
              <div className="preference-item">
                <label>
                  <input type="checkbox" defaultChecked={user?.preferences?.mentions} />
                  Mention Notifications
                </label>
              </div>
              <button className="save-btn">Save Preferences</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;